/* eslint-disable max-lines, react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { css } from 'emotion'
import { matchPath } from 'react-router'
import { Link, withRouter } from 'react-router-dom'
import { Location, History } from 'history'
import Menu from 'antd/es/menu'
import 'antd/es/menu/style/css'
import {
  MAIN_FONT,
  FONT_SIZE,
  FONT_WEIGHT,
  MAIN_COLORS,
  NEUTRAL_COLORS,
} from '@xfers/design-system'
import {
  NavDashboard as NavDashboardIcon,
  NavTransactions as NavTransactionsIcon,
  User as UserIcon,
  DeveloperTool as DeveloperToolIcon,
  Employee as TeamIcon,
  Invoice as InvoiceIcon,
} from '@xfers/design-system/es/icons'
import { createGaEvent } from '~/helpers/gaHelper'

import { PATHS as APP_PATHS } from '~/app/routes/paths'
import { PATHS as BIZ_PATHS } from '~/biz/routes/paths'

import { PERMISSIONS, Route, SubRoute } from '~/types'
import { withPermission } from '~/permission'
import '~/helpers/String'

import { importRoutes } from '~/helpers/dynamicImport'
import FeatureGatingManager from '~/managers/FeatureGatingManager'

type PathsOfEnum = APP_PATHS | BIZ_PATHS

const getTabIcon = (tabName?: string) => {
  const tabIconStyle = { marginRight: 10, width: 16, height: 16 }
  switch (tabName) {
    case 'Dashboard':
      return <NavDashboardIcon style={tabIconStyle} />
    case 'Transactions':
    case 'Credit Cards':
      return <NavTransactionsIcon style={tabIconStyle} />
    case 'Users':
      return <UserIcon style={tabIconStyle} />
    case 'Developer Tools':
      return <DeveloperToolIcon style={tabIconStyle} />
    case 'Team':
      return <TeamIcon style={tabIconStyle} />
    case 'Payment Link':
      return <InvoiceIcon style={tabIconStyle} />
    default:
      return null
  }
}

const CustomMenuItem: React.FC<{
  tab: {
    tabName?: string
    path?: PathsOfEnum
    permission?: PERMISSIONS
    subTabNames?: SubRoute[]
  }
  key: string
  currentPath: PathsOfEnum
  isSandbox: boolean
  isSubMenuEnabled: boolean
}> = ({ tab, key, currentPath, isSandbox, isSubMenuEnabled, children, ...props }) => {
  const matchConfig =
    tab.path === ('/' as PathsOfEnum) ? { path: tab.path, exact: true } : { path: tab.path }
  const match = matchPath(currentPath, matchConfig)

  const WithSubMenu = () => {
    if (isSubMenuEnabled && tab.subTabNames) {
      const hasSubMenu = true
      return (
        <Menu.SubMenu
          title={tab.tabName}
          icon={getTabIcon(tab.tabName)}
          className={SubMenuStyle}
          {...props}
        >
          {tab.subTabNames.map((subMenu, index) => MenuItem(subMenu, `key-${index}`, hasSubMenu))}
        </Menu.SubMenu>
      )
    }
    return MenuItem()
  }

  const MenuItem = (subMenu?: SubRoute, indexKey?: string, hasSubMenu?: boolean) => (
    <Menu.Item
      className={match && !hasSubMenu ? activeLinkClass : ''}
      key={indexKey || key}
      {...props}
    >
      <Link
        data-cy={`link-${tab.tabName?.removeSpace().toCamelCase()}`}
        to={subMenu?.path || tab.path || '/'}
        onClick={() =>
          createGaEvent(
            'General',
            `click${subMenu?.sideBar?.removeSpace() || tab.tabName?.removeSpace()}Link`,
            `Clicked ${subMenu?.sideBar || tab.tabName} in Left Menu`
          )
        }
      >
        <SubMenuTitle isSandbox={isSandbox} isSubMenu={hasSubMenu as boolean}>
          {!subMenu && getTabIcon(tab.tabName)}
          {subMenu?.sideBar || tab.tabName}
        </SubMenuTitle>
      </Link>
      {children}
    </Menu.Item>
  )

  const EnhancedCustomMenu = tab.permission
    ? withPermission(WithSubMenu, tab.permission)
    : WithSubMenu
  return <EnhancedCustomMenu />
}

type NavigationMenuProps = {
  location: Location
  history: History
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match: any
  isSandbox: boolean
}

const NavigationMenu = (props: NavigationMenuProps) => {
  const { pathname } = props.location

  const [openKeys, setOpenKeys] = useState<string[]>([])
  const [routes, setRoutes] = useState<Route[]>([])
  const [isLeftSubMenuEnabled, setIsLeftSubMenuEnabled] = useState<boolean>(false)

  useEffect(() => {
    setUpRoutes()
    async function setUpRoutes() {
      setRoutes(await importRoutes())
    }
    FeatureGatingManager.isEnabled('xfpay_show_left_submenu', window.location.hostname).then(
      resp => {
        setIsLeftSubMenuEnabled(resp)
      }
    )
  }, [isLeftSubMenuEnabled])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onOpenChange = (openKey: any[]) => setOpenKeys(openKey)

  const routesHasTab = routes
    .filter(route => route.sideBar)
    .map(({ path, sideBar, permission, subRoutes }) => ({
      path,
      tabName: sideBar,
      permission,
      subTabNames: subRoutes,
    }))

  return (
    <Container isSandbox={props.isSandbox}>
      <Menu mode="inline" openKeys={openKeys} onOpenChange={onOpenChange}>
        {routesHasTab.map((route, idx) => (
          <CustomMenuItem
            isSandbox={props.isSandbox}
            tab={route}
            key={idx.toString()}
            currentPath={pathname as PathsOfEnum}
            isSubMenuEnabled={isLeftSubMenuEnabled}
          />
        ))}
      </Menu>
    </Container>
  )
}

export default withRouter(NavigationMenu)

const Icon = styled.div`
  width: 25px;
  display: flex;
  align-items: center;
  fill: red;
`

const SubMenuTitle = styled.div`
  transition: 0.2s all ease !important;
  font-family: ${MAIN_FONT};
  font-weight: ${FONT_WEIGHT.MEDIUM};
  font-size: ${FONT_SIZE.P1};
  color: ${(props: { isSandbox: boolean; isSubMenu?: boolean }) =>
    props.isSandbox ? 'white' : NEUTRAL_COLORS.G400};
  padding-left: ${(props: { isSubMenu?: boolean }) => (props.isSubMenu ? '5px' : '15px')};
  display: flex;
  align-items: center;
  border-radius: 5px;
  height: 47px;
  .ant-menu-item &:hover {
    background-color: ${(props: { isSandbox: boolean; isSubMenu?: boolean }) =>
      props.isSandbox ? 'white' : MAIN_COLORS.PRIMARY_BLUE};
    color: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')};
  }
  .ant-menu-item &:hover circle {
    stroke: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')}!important;
  }
  .ant-menu-item &:hover path {
    fill: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')}!important;
  }
  .ant-menu-item &:hover .company rect.outline {
    fill: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')};!important;
  }
  .ant-menu-item &:hover .company path.door {
    stroke: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? '#263a52' : 'white'} !important;
    fill: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? 'white' : MAIN_COLORS.PRIMARY_BLUE} !important;
  }
  .ant-menu-item &:hover .company path.hider {
    fill: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? 'white' : MAIN_COLORS.PRIMARY_BLUE} !important;
  }
  .ant-menu-item &:hover .user path {
    stroke:  ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? '#263a52' : 'white'} !important;
    fill-opacity: 0 !important;
  }
`

const SubMenuStyle = css`
  transition: 0.2s all ease !important;
  font-family: ${MAIN_FONT};
  font-weight: ${FONT_WEIGHT.MEDIUM};
  font-size: ${FONT_SIZE.P1};
  color: ${NEUTRAL_COLORS.G400};
  border-radius: 5px;
`

const Container = styled.div`
  padding-bottom: 45px;
  & .ant-menu-inline {
    border: 0;
    background: ${(props: { isSandbox: boolean; isSubMenu?: boolean }) =>
      props.isSandbox ? '#263a52' : 'white'};
  }
  & .ant-menu-inline > .ant-menu-submenu > .ant-menu-submenu-title {
    height: 47px;
    margin: 0;
    padding-left: 14px !important;
  }
  & .ant-menu-inline > .ant-menu-submenu {
    padding-top: 7px;
    padding-bottom: 7px;
  }

  & .ant-menu-inline .ant-menu-item {
    height: unset;
    margin: 0;
    padding-bottom: 7px;
    padding-top: 7px;
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  & .ant-menu-inline .ant-menu-submenu {
    height: unset;
    margin: 0 0 8px 0;
    padding-bottom: 7px;
    padding-top: 7px;
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  & .ant-menu-submenu-title {
    display: flex;
    align-items: center;
    border-radius: 5px;
    color: ${(props: { isSandbox: boolean }) => (props.isSandbox ? 'white' : NEUTRAL_COLORS.G400)};
    :hover {
      color: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')};
      background-color: ${(props: { isSandbox: boolean }) =>
        props.isSandbox ? 'white' : MAIN_COLORS.PRIMARY_BLUE};
    }
  }

  & .ant-menu-submenu-inline > .ant-menu-submenu-title:hover .ant-menu-submenu-arrow::after {
    background: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')};
  }
  & .ant-menu-submenu-inline > .ant-menu-submenu-title:hover .ant-menu-submenu-arrow::before {
    background: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')};
  }
  & .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow::after {
    background: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? 'white' : NEUTRAL_COLORS.G400};
  }
  & .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow::before {
    background: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? 'white' : NEUTRAL_COLORS.G400};
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
    background: transparent;
  }
  & .ant-menu-inline .ant-menu-item-selected::after {
    display: none;
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected ${SubMenuTitle} {
    background-color: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? 'white' : MAIN_COLORS.PRIMARY_BLUE};
    color: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')};
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected ${Icon} circle {
    stroke: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')}!important;
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected ${Icon} path {
    fill: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')}!important;
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected ${Icon} .company rect.outline {
    fill: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')} !important;
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected ${Icon} path.door {
    stroke: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? '#263a52' : 'white'} !important;
    fill: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? 'white' : MAIN_COLORS.PRIMARY_BLUE} !important;
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected ${Icon} path.hider {
    fill: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? 'white' : MAIN_COLORS.PRIMARY_BLUE} !important;
  }
  & .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected ${Icon} .user path {
    stroke: ${(props: { isSandbox: boolean }) =>
      props.isSandbox ? '#263a52' : 'white'} !important;
    fill-opacity: 0 !important;
  }
  & .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow {
    right: 30px;
    line-height: 47px;
  }
  & .ant-menu-sub.ant-menu-inline > .ant-menu-item {
    height: 54px;
    padding-left: 40px !important;
  }
  & .ant-menu-submenu > .ant-menu {
    background: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '#263a52' : 'white')};
  }
  & .ant-menu-item > a::before {
    display: none;
  }
`
const activeLinkClass = css`
  &.ant-menu-item ${SubMenuTitle} {
    background-color: ${MAIN_COLORS.PRIMARY_BLUE};
    color: white;
  }
  &.ant-menu-item ${SubMenuTitle} circle {
    stroke: white;
  }
  &.ant-menu-item ${SubMenuTitle} path {
    fill: white !important;
  }
  &.ant-menu-item ${SubMenuTitle} .company rect.outline {
    fill: white !important;
  }
  &.ant-menu-item ${SubMenuTitle} .company path.door {
    stroke: white !important;
    fill: ${MAIN_COLORS.PRIMARY_BLUE} !important;
  }
  &.ant-menu-item ${SubMenuTitle} .company path.hider {
    fill: ${MAIN_COLORS.PRIMARY_BLUE} !important;
  }
  &.ant-menu-item ${SubMenuTitle} .user path {
    stroke: white !important;
    fill-opacity: 0 !important;
  }
`
